import { useEffect, useState } from 'react'
import { swimApi } from '@swim/api-client'
import type { OrbSignals } from '@swim/ui'
import { useSession } from './use-session'

/**
 * Fetches and subscribes to live orb signals (unread messages, notifications, drops balance).
 *
 * Behavior:
 * 1. If unauthenticated: returns null (no signals)
 * 2. On mount: bootstrap with GET /api/signals
 * 3. Immediately open SSE stream via /api/notifications/stream
 * 4. Listen for 'signals' events and update state
 * 5. On unmount: close SSE connection
 *
 * Returns null on auth failure or fetch error — orb renders in idle state (graceful degradation).
 */
export function useOrbSignals(): OrbSignals | null {
  const { user } = useSession()
  const [signals, setSignals] = useState<OrbSignals | null>(null)

  useEffect(() => {
    if (!user?.id) {
      setSignals(null)
      return
    }

    let cancelled = false

    async function connect() {
      try {
        // 1. Bootstrap current signals
        const bootstrapResult = await swimApi.get<OrbSignals>('/signals')
        if (!cancelled && bootstrapResult.success) {
          setSignals(bootstrapResult.data)
        }

        // SSE stream disabled until Web-1kr2 (self-hosted Redis) is shipped
      } catch (err) {
        // non-blocking: signals unavailable — orb stays in idle state
        console.warn('[useOrbSignals] Failed to connect:', err)
      }
    }

    void connect()

    return () => {
      cancelled = true
    }
  }, [user?.id])

  return signals
}
